import { X, Lock, Crown, ArrowUpCircle } from 'lucide-react'
import { useNavigate } from 'react-router-dom'

interface RecursoBloquadoModalProps {
    isOpen: boolean
    onClose: () => void
    recursoNome: string
    isTrial?: boolean
    planoAtual?: string
}

export default function RecursoBloquadoModal({
    isOpen,
    onClose,
    recursoNome,
    isTrial = true,
    planoAtual = 'Start'
}: RecursoBloquadoModalProps) {
    if (!isOpen) return null

    const navigate = useNavigate()

    const handleAction = () => {
        if (isTrial) {
            onClose()
        } else {
            navigate('/assinar')
        }
    }

    // Capitaliza a primeira letra do plano
    const planoFormatado = planoAtual.charAt(0).toUpperCase() + planoAtual.slice(1)

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full p-8 relative animate-fadeIn">
                {/* Botão Fechar */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                >
                    <X className="w-6 h-6" />
                </button>

                {/* Ícone */}
                <div className="flex justify-center mb-6">
                    <div className={`w-20 h-20 rounded-full flex items-center justify-center ${isTrial
                        ? 'bg-gradient-to-br from-orange-100 to-orange-200 dark:from-orange-900/30 dark:to-orange-800/30'
                        : 'bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900/30 dark:to-purple-800/30'
                        }`}>
                        {isTrial ? (
                            <Lock className="w-10 h-10 text-orange-600 dark:text-orange-400" />
                        ) : (
                            <ArrowUpCircle className="w-10 h-10 text-purple-600 dark:text-purple-400" />
                        )}
                    </div>
                </div>

                {/* Título */}
                <h2 className="text-2xl font-bold text-center text-gray-900 dark:text-gray-100 mb-4">
                    {isTrial ? 'Recurso Bloqueado' : 'Faça Upgrade do Plano'}
                </h2>

                {/* Mensagem */}
                <div className="text-center space-y-4 mb-6">
                    <p className="text-gray-600 dark:text-gray-400">
                        {isTrial ? (
                            <>
                                O recurso <strong className="text-gray-900 dark:text-gray-100">{recursoNome}</strong> estará disponível após o período de teste e ativação de sua assinatura.
                            </>
                        ) : (
                            <>
                                O recurso <strong className="text-gray-900 dark:text-gray-100">{recursoNome}</strong> não está incluído no seu plano atual (<strong>{planoFormatado}</strong>).
                            </>
                        )}
                    </p>

                    <div className={`border-2 rounded-xl p-4 ${isTrial
                        ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800'
                        : 'bg-purple-50 dark:bg-purple-900/20 border-purple-200 dark:border-purple-800'
                        }`}>
                        <div className="flex items-start gap-3">
                            <Crown className={`w-5 h-5 flex-shrink-0 mt-0.5 ${isTrial ? 'text-blue-600 dark:text-blue-400' : 'text-purple-600 dark:text-purple-400'
                                }`} />
                            <div className="text-left">
                                <p className={`text-sm font-semibold mb-1 ${isTrial ? 'text-blue-900 dark:text-blue-100' : 'text-purple-900 dark:text-purple-100'
                                    }`}>
                                    {isTrial ? 'Aproveite seu período de teste!' : 'Desbloqueie todo o potencial'}
                                </p>
                                <p className={`text-xs ${isTrial ? 'text-blue-700 dark:text-blue-300' : 'text-purple-700 dark:text-purple-300'
                                    }`}>
                                    {isTrial ? (
                                        <>
                                            Você está testando o plano <strong>Start</strong> gratuitamente por 7 dias.
                                            Após esse período, ative sua assinatura para ter acesso completo a todos os recursos. :)
                                        </>
                                    ) : (
                                        <>
                                            Faça um upgrade para o plano <strong>Medium</strong> ou <strong>Pro</strong> para ter acesso ilimitado a este e outros recursos exclusivos.
                                        </>
                                    )}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Botão */}
                <button
                    onClick={handleAction}
                    className={`w-full text-white font-semibold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] active:scale-[0.98] shadow-lg ${isTrial
                        ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800'
                        : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
                        }`}
                >
                    {isTrial ? 'Entendi' : 'Ver Planos Disponíveis'}
                </button>
            </div>
        </div>
    )
}
